import { useGeneralSettingsFormData } from "@/api/cms";
import {
  getMainLanguage,
  isSupportedLocale,
  supportedLocales,
  SupportedLocale,
  toSupportedLocale,
} from "@/i18n";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Website languages for multilingual BO/CMS fields.
 *
 * `websiteLanguages` / enabled list comes from CMS general settings.
 * `defaultWebsiteLanguage` (primary / left column in DoubleMultilingualField)
 * follows Instance.mainLanguage (i18n), with CMS value as fallback.
 */
export function useCmsWebsiteLanguages() {
  const { data, isLoading } = useGeneralSettingsFormData();
  const { i18n } = useTranslation();

  // On mémoïse sur les VALEURS de langue, pas sur l'objet `data` : la réponse
  // de general-settings contient des URL S3 signées (faviconUrl,
  // maintenanceImageUrl) régénérées à chaque requête, donc `data` change
  // d'identité à chaque refetch. Dépendre de `data` faisait recréer les
  // tableaux de langues en permanence (remontages parasites des champs
  // multilingues, perte de focus).
  const cmsDefaultRaw = data?.defaultWebsiteLanguage;
  const enabledKey = data?.websiteLanguages?.join(",");

  return useMemo(() => {
    const cmsDefault = (cmsDefaultRaw ?? "fr") as SupportedLocale;
    const instancePrimary = toSupportedLocale(
      i18n.language || getMainLanguage(),
    );

    const enabledFromCms = (
      enabledKey ? enabledKey.split(",") : [...supportedLocales]
    ) as SupportedLocale[];

    // Instance mainLanguage always drives the primary (left) column in BO forms.
    const defaultWebsiteLanguage = isSupportedLocale(instancePrimary)
      ? instancePrimary
      : cmsDefault;

    const websiteLanguages = [
      defaultWebsiteLanguage,
      ...enabledFromCms.filter((lang) => lang !== defaultWebsiteLanguage),
    ] as SupportedLocale[];

    const secondaryLanguages = websiteLanguages.filter(
      (lang) => lang !== defaultWebsiteLanguage,
    );

    return {
      defaultWebsiteLanguage,
      cmsDefaultWebsiteLanguage: cmsDefault,
      websiteLanguages,
      secondaryLanguages,
      isLoading,
    };
  }, [cmsDefaultRaw, enabledKey, i18n.language, isLoading]);
}
